import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        /* --- Amigo Inn brand variants --- */
        book: "rounded-sm bg-clay text-accent-foreground font-semibold tracking-wide uppercase text-xs shadow-[var(--shadow-soft)] hover:brightness-[1.07] active:brightness-95",
        forest:
          "rounded-sm bg-forest text-primary-foreground font-semibold tracking-wide uppercase text-xs shadow-[var(--shadow-soft)] hover:bg-forest-deep",
        cream:
          "rounded-sm bg-cream text-forest-deep font-semibold tracking-wide uppercase text-xs shadow-[var(--shadow-soft)] hover:bg-cream-deep",
        outlineLight:
          "rounded-sm border border-cream/60 bg-transparent text-cream font-semibold tracking-wide uppercase text-xs backdrop-blur-[2px] hover:bg-cream/12 hover:border-cream",
        outlineForest:
          "rounded-sm border border-forest/35 bg-transparent text-forest font-semibold tracking-wide uppercase text-xs hover:bg-forest hover:text-primary-foreground hover:border-forest",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8",
        icon: "h-9 w-9",
        /* Brand sizes with 44px+ touch targets */
        cta: "h-12 px-7",
        ctaLg: "h-14 px-9 text-sm",
        tap: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);


export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
